import type { RevenueMetricKey } from '../types/revenue.js';

/**
 * 売上指標1件の定義情報。ビジネスサイド（会計士）向けの「収益認識の考え方」とエンジニア向けの「技術定義」を持つ。
 * label / block は API レスポンス columns（REVENUE_COLUMNS 由来の単一ソース）から取得するため、
 * 定義データは type / businessDescription / technicalSource / validation に限定する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計各指標のビジネス定義・技術定義を一元管理する型
 * @graph-connects none
 */
export interface RevenueMetricDefinition {
  type: 'stock' | 'flow';
  businessDescription: string;
  technicalSource: {
    database: string;
    tables: string[];
    logic: string;
  };
  validation?: string;
}

/**
 * 売上集計 12指標の定義一覧。ビジネス定義と技術定義の単一ソース。
 * Record<RevenueMetricKey, ...> により RevenueMetricKey に指標が追加されるとコンパイルエラーで検知できる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計の12指標について、ビジネス定義（収益認識基準）と技術定義（ソースDB・テーブル・FIFO対応付けロジック）を保持する
 * @graph-connects none
 */
export const REVENUE_DEFINITIONS: Record<RevenueMetricKey, RevenueMetricDefinition> = {
  subscriptionFeeMonthly: {
    type: 'flow',
    businessDescription:
      '収益認識基準: 役務提供完了基準。月1回交換プランの月額会費は、課金時点では収益認識せず前受金（契約負債）として計上する。レンタルアイテムの発送完了（＝役務提供完了）をもって、前受金から売上高へ振り替える。対応付け: 課金と交換権はFIFO（先入先出法）で対応付ける。金額: 税抜額。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / phoenix_order (MySQL)',
      tables: ['payment_histories', 'user_ticket_relations', 'delivery_orders'],
      logic:
        "ユーザーごとに、確定済み(payment_type_code=subscription_fee, plan_frequency=monthly)の課金を購入日昇順、subscriptionチケット(plan_frequency=monthly)を作成日昇順に並べ、先頭同士をFIFOで1:1対応付ける。対応付けたチケットが使用済み(is_used=true)かつ紐づく注文が発送完了(delivery_orders.order_status=7)している場合、発送完了日(delivered_date, JST)を売上認識日として課金額(original_price)を計上する。\n// 擬似SQL\nSELECT delivered_date::date AS date, SUM(original_price)\nFROM fifo_matched(payments WHERE payment_type_code='subscription_fee' AND plan_frequency='monthly' AND confirmed,\n                   tickets WHERE ticket_type='subscription' AND plan_frequency='monthly')\nWHERE is_used = true AND delivered_date IS NOT NULL\nGROUP BY date",
    },
  },
  subscriptionFeeBimonthly: {
    type: 'flow',
    businessDescription:
      '収益認識基準: 課金確定時一括認識。2ヶ月に1回交換プランの月額会費は、課金確定時点で全額を売上高として認識する。前受金処理は行わない。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / payment (MySQL)',
      tables: ['payment_histories', 'user_payment_histories'],
      logic:
        "payment_type_code = 'subscription_fee' かつ plan_frequency = 'bimonthly' の課金のうち、payment DB側で決済確定(paid_status=2 AND paid_flag=1)しているものを購入日(purchase_date, JST)で日次合計する。\nSELECT purchase_date::date AS date, SUM(original_price)\nFROM payment_histories\nWHERE payment_type_code = 'subscription_fee' AND plan_frequency = 'bimonthly' AND confirmed\nGROUP BY date",
    },
  },
  ticketPurchaseRevenue: {
    type: 'flow',
    businessDescription:
      '収益認識基準: 役務提供完了基準。追加交換チケット（¥15,800/枚、有効期限6ヶ月）の購入代金は、課金時点で前受金（契約負債）として計上する。チケット消化（発送完了）時に売上高へ振り替える。失効時は失効売上に振替。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / phoenix_order (MySQL)',
      tables: ['payment_histories', 'user_ticket_relations', 'delivery_orders'],
      logic:
        "purchaseチケット(ticket_type='purchase')は payment_history_id で課金(payment_type_code='ticket_purchase')に直結する（FIFO不要、1:1）。対応するチケットが使用済み(is_used=true)かつ紐づく注文が発送完了している場合、発送完了日(delivered_date, JST)を売上認識日として課金額を計上する。\nSELECT delivered_date::date AS date, SUM(original_price)\nFROM tickets t JOIN payment_histories p ON p.id = t.payment_history_id\nWHERE t.ticket_type = 'purchase' AND t.is_used = true AND delivered_date IS NOT NULL\nGROUP BY date",
    },
  },
  penaltyFee: {
    type: 'flow',
    businessDescription:
      '収益認識基準: 課金確定時一括認識。年間プランの契約期間中途解約に伴う違約金。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / payment (MySQL)',
      tables: ['payment_histories', 'user_payment_histories'],
      logic:
        "payment_type_code = 'penalty_fee' の確定済み課金(paid_status=2 AND paid_flag=1)を購入日(purchase_date, JST)で日次合計する。\nSELECT purchase_date::date AS date, SUM(original_price)\nFROM payment_histories WHERE payment_type_code = 'penalty_fee' AND confirmed\nGROUP BY date",
    },
  },
  itemPurchase: {
    type: 'flow',
    businessDescription: '収益認識基準: 引渡基準。レンタル中アイテムのユーザー買取に伴う売上。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / payment (MySQL)',
      tables: ['payment_histories', 'user_payment_histories'],
      logic:
        "payment_type_code = 'item_purchase' の確定済み課金(paid_status=2 AND paid_flag=1)を購入日(purchase_date, JST)で日次合計する。\nSELECT purchase_date::date AS date, SUM(original_price)\nFROM payment_histories WHERE payment_type_code = 'item_purchase' AND confirmed\nGROUP BY date",
    },
  },
  expiredRevenue: {
    type: 'flow',
    businessDescription:
      '収益認識基準: 権利失効時認識。交換権が未使用のまま失効した場合に前受金から振り替えて認識する売上。失効事由: (1)有効期限経過 (2)退会による権利消滅（退会日で即時失効）。会費売上・チケット購入売上とは区分表示。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_ticket_relations', 'user_subscription_plan_relations'],
      logic:
        'FIFO対応付け済みのsubscription/purchaseチケットのうち is_used=false かつ expired_at が設定されているものを対象に、失効日を判定する。ユーザーの退会日(cancelled_at)が有効期限(expired_at)より前なら退会日を、そうでなければ有効期限日を失効日(JST)として、対応する課金額を計上する。\nSELECT expired_date::date AS date, SUM(original_price)\nFROM fifo_matched_tickets WHERE is_used = false AND expired_at IS NOT NULL\nGROUP BY date -- expired_date = LEAST(cancelled_at, expired_at) 相当',
    },
  },
  badDebt: {
    type: 'flow',
    businessDescription:
      '定義: 課金処理を実行したが決済が失敗（FAIL/DROP）し、回収不能となった金額。リトライにより回収された場合は badDebtRecovery（貸倒回収）で消し込む。計上日: 失敗した決済のpurchase_date（JST）。純売上からマイナス控除。',
    technicalSource: {
      database: 'payment (MySQL)',
      tables: ['user_payment_histories', 'payment_cycles'],
      logic:
        'user_payment_histories のうち paid_status IN (3, 9)（FAIL/DROP）の行を、対応する aircloset_mens.payment_histories の purchase_date(JST) に紐付けて日次合計する。同一 payment_cycle_id に SUCCESS(paid_status=2, paid_flag=1) が存在する場合も計上するが、その回収額は bad_debt_recovery で相殺される。当該FAILレコードのoriginal_priceは売上からも除外する（二重計上防止）。',
    },
  },
  badDebtRecovery: {
    type: 'flow',
    businessDescription:
      '定義: 一度決済失敗（FAIL/DROP）した課金が、リトライにより回収できた金額。bad_debt の消し込み（戻入）として機能する。純貸倒 = bad_debt − bad_debt_recovery。計上日: リトライ成功した決済のpurchase_date（JST）。実績: 決済失敗の85%は翌日のリトライバッチで回収される。',
    technicalSource: {
      database: 'payment (MySQL)',
      tables: ['user_payment_histories', 'payment_cycles'],
      logic:
        '同一 payment_cycle_id に FAIL/DROP(paid_status IN (3,9)) と SUCCESS(paid_status=2, paid_flag=1) の両方が存在する場合、SUCCESS 側のレコードを「リトライ回収」として抽出する。対応する aircloset_mens.payment_histories の original_price を、SUCCESS 側の purchase_date(JST) で日次合計する。当該SUCCESSレコードのoriginal_priceは売上からも除外する（二重計上防止）。',
    },
  },
  refund: {
    type: 'flow',
    businessDescription: '定義: 確定済み課金に対する事後の返金処理。純売上からマイナス控除。',
    technicalSource: {
      database: 'payment (MySQL)',
      tables: ['cancel_payment_histories', 'user_payment_histories'],
      logic:
        'cancel_payment_histories（クレジット/Amazon v1返金）の canceled_date(JST) と、user_payment_histories の負price行（Amazon v1.1返金）の created_at(JST) の両方を対象に、絶対値を日次合計する。\nSELECT canceled_date::date AS date, SUM(ABS(price))\nFROM cancel_payment_histories\nGROUP BY date\nUNION ALL\nSELECT created_at::date AS date, SUM(ABS(price))\nFROM user_payment_histories WHERE price < 0\nGROUP BY date',
    },
  },
  deferredInflowSubscription: {
    type: 'flow',
    businessDescription:
      '定義: 月1回プランの当日課金確定額。課金確定時点で前受金（契約負債）に計上。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / payment (MySQL)',
      tables: ['payment_histories', 'user_payment_histories'],
      logic:
        "payment_type_code = 'subscription_fee' かつ plan_frequency = 'monthly' の確定済み課金を購入日(purchase_date, JST)で日次合計する。\nSELECT purchase_date::date AS date, SUM(original_price)\nFROM payment_histories WHERE payment_type_code = 'subscription_fee' AND plan_frequency = 'monthly' AND confirmed\nGROUP BY date",
    },
  },
  deferredInflowTicket: {
    type: 'flow',
    businessDescription: '定義: チケット購入の当日課金確定額。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL) / payment (MySQL)',
      tables: ['payment_histories', 'user_payment_histories'],
      logic:
        "payment_type_code = 'ticket_purchase' の確定済み課金を購入日(purchase_date, JST)で日次合計する。\nSELECT purchase_date::date AS date, SUM(original_price)\nFROM payment_histories WHERE payment_type_code = 'ticket_purchase' AND confirmed\nGROUP BY date",
    },
  },
  deferredBalanceSubscription: {
    type: 'stock',
    businessDescription: '定義: 月1回プランの課金済み・未振替金額の累計残高。',
    technicalSource: {
      database: '（導出値）',
      tables: [],
      logic:
        'アプリ側で日次バッチ実行時に全期間を再計算する。deferred_balance_subscription[d] = Σ(deferred_inflow_subscription[0..d]) − Σ(subscription_fee_monthly[0..d]) − Σ(expired_revenue[0..d])',
    },
    validation: '恒等式: 前受金残高 = 累計流入額 − 累計売上振替額 − 累計失効振替額 ≥ 0',
  },
  deferredBalanceTicket: {
    type: 'stock',
    businessDescription: '定義: チケット購入の課金済み・未振替金額の累計残高。',
    technicalSource: {
      database: '（導出値）',
      tables: [],
      logic:
        'アプリ側で日次バッチ実行時に全期間を再計算する。deferred_balance_ticket[d] = Σ(deferred_inflow_ticket[0..d]) − Σ(ticket_purchase_revenue[0..d])',
    },
    validation: '恒等式: 前受金残高 = 累計購入額 − 累計消化額 − 累計失効額 ≥ 0',
  },
};
